import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { SearchSelect } from '../../components/search-select.js';
import { toIsoEndOfDayOrNull, toIsoOrNull } from '../../lib/date-input.js';
import { plansApi } from '../../lib/plans-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { substitutionsApi } from '../../lib/substitutions-api.js';

// #50 (PLAN-5): Vertretung bedienbar machen. Platzierung laut Nutzerentscheidung in der
// Testplan-Zuweisung (plan-detail unter der Scope-Tabelle) — nah an Playlist/Ownership, dort
// entsteht der Bedarf. Anlegen/Beenden sind `plan.manage` (Backend erzwingt es zusätzlich); ohne
// das Recht wird der Abschnitt gar nicht gezeigt. Die Mitgliederliste wird mit der ScopeTable
// geteilt (queryKey ['plan-members', projectId]).

function formatDay(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function PlanSubstitutions({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useScopedUser(projectId);
  const queryClient = useQueryClient();

  const canManage = user ? canInAnyScope(user, 'plan.manage') : false;

  const { data: subs } = useQuery({
    queryKey: ['substitutions', projectId],
    queryFn: () => substitutionsApi.list(projectId),
    enabled: Boolean(projectId) && canManage,
  });
  const { data: members } = useQuery({
    queryKey: ['plan-members', projectId],
    queryFn: () => plansApi.members(projectId),
    enabled: Boolean(projectId) && canManage,
  });

  const [absent, setAbsent] = useState('');
  const [substitute, setSubstitute] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // #96: Rückfrage vor dem Beenden einer Vertretung — nennt die abwesende Person beim Namen.
  const [confirmEnd, setConfirmEnd] = useState<{ id: string; name: string } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['substitutions', projectId] });
  };

  const create = useMutation({
    mutationFn: () =>
      substitutionsApi.create(projectId, {
        absent_user_id: absent,
        substitute_user_id: substitute,
        // Von = Tagesbeginn, Bis = Tagesende: ein Ein-Tages-Fenster (from == to) soll den ganzen
        // Tag aktiv sein, nicht nur um Mitternacht (siehe toIsoEndOfDayOrNull).
        from_date: toIsoOrNull(fromDate) ?? '',
        to_date: toIsoEndOfDayOrNull(toDate) ?? '',
      }),
    meta: { successToast: 'feedback.created' },
    onSuccess: () => {
      invalidate();
      setAbsent('');
      setSubstitute('');
      setFromDate('');
      setToDate('');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => substitutionsApi.remove(projectId, id),
    meta: { successToast: 'feedback.deleted' },
    onSuccess: () => {
      invalidate();
      setConfirmEnd(null);
    },
  });

  // Dieselben zwei Regeln wie der Server-Refine (SubstitutionCreateSchema) — hier für sofortiges,
  // benanntes Feedback, statt einen 400 abzuwarten. Der Server bleibt die Absicherung.
  const sameUser = absent !== '' && absent === substitute;
  const badOrder = fromDate !== '' && toDate !== '' && fromDate > toDate;
  const complete = absent !== '' && substitute !== '' && fromDate !== '' && toDate !== '';
  const createDisabled = !complete || sameUser || badOrder || create.isPending;

  const memberName = useMemo(() => {
    const m = new Map((members ?? []).map((x) => [x.user_id, x.name]));
    return (id: string, fallback: string | null) =>
      m.get(id) ?? fallback ?? t('plans.substitutions.unknownUser');
  }, [members, t]);

  if (!canManage) return null;

  const rows = subs ?? [];
  const memberOptions = (members ?? []).map((m) => ({ value: m.user_id, label: m.name }));

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface">
      <div className="border-b border-[var(--border)] px-4 py-3">
        <span className="text-sm font-semibold text-text-primary">
          {t('plans.substitutions.title')}
        </span>
        <p className="mt-1 text-[11px] leading-snug text-text-muted">
          {t('plans.substitutions.hint')}
        </p>
      </div>

      {/* Anlege-Formular */}
      <div className="flex flex-wrap items-end gap-2 border-b border-[var(--border)] px-4 py-3">
        <div className="flex flex-col gap-1 text-xs text-text-muted">
          {t('plans.substitutions.absent')}
          <SearchSelect
            label={t('plans.substitutions.absent')}
            value={absent}
            onChange={setAbsent}
            options={memberOptions}
            emptyLabel={t('plans.substitutions.select')}
            className="w-48 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </div>
        <div className="flex flex-col gap-1 text-xs text-text-muted">
          {t('plans.substitutions.substitute')}
          <SearchSelect
            label={t('plans.substitutions.substitute')}
            value={substitute}
            onChange={setSubstitute}
            options={memberOptions}
            emptyLabel={t('plans.substitutions.select')}
            className="w-48 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </div>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('plans.substitutions.from')}
          <input
            type="date"
            aria-label={t('plans.substitutions.from')}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('plans.substitutions.to')}
          <input
            type="date"
            aria-label={t('plans.substitutions.to')}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={createDisabled}
          className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t('plans.substitutions.add')}
        </button>
      </div>

      {(sameUser || badOrder) && (
        <div className="border-b border-[var(--border)] px-4 py-2 text-xs text-status-fail">
          {sameUser
            ? t('plans.substitutions.sameUserError')
            : t('plans.substitutions.dateOrderError')}
        </div>
      )}

      {/* Bestehende Vertretungen */}
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-xs text-text-muted">{t('plans.substitutions.empty')}</div>
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {rows.map((s) => (
            <li
              key={s.substitution_id}
              className="flex items-center justify-between gap-2 px-4 py-2"
            >
              <span className="text-sm text-text-body">
                {t('plans.substitutions.row', {
                  substitute: memberName(s.substitute_user_id, s.substitute_name),
                  absent: memberName(s.absent_user_id, s.absent_name),
                })}
                <span className="ml-2 text-[11px] text-text-muted">
                  {t('plans.substitutions.period', {
                    from: formatDay(s.from_date, i18n.language),
                    to: formatDay(s.to_date, i18n.language),
                  })}
                </span>
              </span>
              <button
                type="button"
                aria-label={t('plans.substitutions.endFor', {
                  name: memberName(s.absent_user_id, s.absent_name),
                })}
                onClick={() =>
                  setConfirmEnd({
                    id: s.substitution_id,
                    name: memberName(s.absent_user_id, s.absent_name),
                  })
                }
                className="shrink-0 rounded-btn px-3 py-1 text-sm text-status-fail hover:underline"
              >
                {t('plans.substitutions.end')}
              </button>
            </li>
          ))}
        </ul>
      )}

      {confirmEnd && (
        <ConfirmDialog
          title={t('plans.substitutions.end')}
          message={t('plans.substitutions.confirmEnd', { name: confirmEnd.name })}
          confirmLabel={t('plans.substitutions.end')}
          danger
          pending={remove.isPending}
          onCancel={() => setConfirmEnd(null)}
          onConfirm={() => remove.mutate(confirmEnd.id)}
        />
      )}
    </div>
  );
}
